import { useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useTranslation } from "react-i18next"

import { logoutApi } from "@/api/api"
import { FeedbackDialog } from "@/components/feedback-dialog"
import {
  ChevronsUpDown,
  LogOut,
  Megaphone,
  MessageSquare,
  Settings,
  User as UserIcon,
} from "@/components/icons"
import { Avatar } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"
import { useSidebar } from "@/components/ui/use-sidebar"
import { PreferencesDialog } from "@/features/personalization/components/preferences-dialog"
import { useSessionId } from "@/hooks/use-session-id"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"
import { usePreferencesStore } from "@/stores/preferences-store"
import { useWhatsNewStore } from "@/stores/whats-new-store"

export function NavUser() {
  const { isMobile } = useSidebar()
  const { t } = useTranslation()
  const { instance } = useMsal()
  const settingsService = SettingsService.getInstance()
  const activeAccount = instance.getActiveAccount()
  const {
    open: preferencesOpen,
    initialTab: preferencesInitialTab,
    setOpen: setPreferencesOpen,
    openWith: openPreferencesWith,
  } = usePreferencesStore()
  const [showFeedback, setShowFeedback] = useState(false)
  const { setOpen: openWhatsNew } = useWhatsNewStore()
  // The session the user is currently looking at, if any (null outside a
  // chat/module route). Lets support find the matching server logs even when
  // the report is opened from the user menu rather than an in-chat action
  // that already carries a correlation id (E20, #3325).
  const sessionId = useSessionId()

  const user = {
    name: settingsService.claims.name,
    email:
      settingsService.claims.preferred_username ??
      settingsService.claims.email ??
      settingsService.claims.unique_name,
    avatar: `https://robohash.org/${settingsService.claims.name}.png?size=64x64`,
  }

  async function handleLogout() {
    if (settingsService.usingMsalBackup) {
      if (activeAccount) {
        instance
          .logoutRedirect({
            postLogoutRedirectUri: "/", // redirects the top level app after logout
            account: instance.getActiveAccount(),
          })
          .catch((error) => logger.error("Error logging out", error as Error))
      }
    } else {
      await logoutApi()
    }
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                data-testid="user-menu"
                size="lg"
              >
                <Avatar
                  className="flex size-8 items-center justify-center rounded-lg"
                  data-testid="user-avatar"
                >
                  <UserIcon className="size-5" />
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold" data-testid="user-name">
                    {user.name}
                  </span>
                  <span className="truncate text-xs" data-testid="user-email">
                    {user.email}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="flex size-8 items-center justify-center rounded-lg">
                    <UserIcon className="size-5" />
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user.name}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="py-2"
                data-testid="preferences-menu-item"
                onClick={() => openPreferencesWith("general")}
              >
                <Settings className="size-5" />
                {t("personalization.preferences.menu", { defaultValue: "Preferences" })}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="py-2"
                data-testid="whats-new-menu-item"
                onClick={() => openWhatsNew(true)}
              >
                <Megaphone className="size-5" />
                {t("pages.whats_new.menu")}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="py-2"
                data-testid="feedback-menu-item"
                onClick={() => setShowFeedback(true)}
              >
                <MessageSquare className="size-5" />
                {t("feedback.menu", "Send feedback")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="py-2"
                data-testid="logout-menu-item"
                onClick={handleLogout}
              >
                <LogOut className="size-5" />
                {t("loginbutton.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <PreferencesDialog
        initialTab={preferencesInitialTab}
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
      />
      <FeedbackDialog
        hideTrigger
        open={showFeedback}
        prefillContext={{ sessionId }}
        onOpenChange={setShowFeedback}
      />
    </>
  )
}
